import PhotoSwipe from 'photoswipe';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipeVideoPlugin from 'photoswipe-video-plugin';
import { SWIPER } from '../../utils/constants';

let lightbox = null;
let isLightboxInitialized = false;

// A poster image is required for the video plugin to ever mark a slide as
// loaded (see photoswipe-video-plugin's preloadVideoPoster) — fall back to a
// blank pixel so slides without a Shopify-generated preview image still load.
const BLANK_POSTER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBTAA7';

const buildSlideData = (slideEl) => {
  const { lightboxImage, lightboxVideoSrc, lightboxWidth, lightboxHeight, lightboxPoster } = slideEl.dataset;
  const width = Number(lightboxWidth);
  const height = Number(lightboxHeight);
  if (!width || !height) return null;

  if (lightboxVideoSrc) {
    return { type: 'video', videoSrc: lightboxVideoSrc, width, height, msrc: lightboxPoster || BLANK_POSTER };
  }

  if (lightboxImage) {
    const slideImageEl = slideEl.querySelector('img');
    return { src: lightboxImage, width, height, msrc: slideImageEl?.currentSrc };
  }

  return null;
};

const getLightbox = () => {
  if (lightbox) return lightbox;

  lightbox = new PhotoSwipeLightbox({
    pswpModule: PhotoSwipe,
    // Close only via the close button — not by clicking the backdrop or a
    // non-zoomable slide (e.g. video, or an image already at its zoom limit).
    bgClickAction: false,
    clickToCloseNonZoomable: false,
    // PhotoSwipe's default "fit" zoom never upscales past a slide's native
    // resolution, so a lower-resolution image displays smaller than its
    // neighbours instead of matching them — always fit the viewport instead.
    initialZoomLevel: (zoomLevel) => {
      const { panAreaSize, elementSize } = zoomLevel;
      return Math.min(panAreaSize.x / elementSize.x, panAreaSize.y / elementSize.y);
    },
  });
  // the plugin registers itself onto the lightbox via events — no reference needed
  new PhotoSwipeVideoPlugin(lightbox, {});

  lightbox.init();

  return lightbox;
};

const openGalleryLightbox = (galleryEl, clickedSlideEl) => {
  const slideEls = [...galleryEl.querySelectorAll(SWIPER.SLIDE_CLASS)];
  const slides = slideEls
    .map((slideEl) => ({ slideEl, data: buildSlideData(slideEl) }))
    .filter((slide) => slide.data);
  if (!slides.length) return;

  // On desktop the gallery is a static grid (Swiper only runs below the
  // desktop breakpoint), so `swiper-slide-active` never exists there — use
  // the slide that was actually clicked instead.
  const activeIndex = slides.findIndex((slide) => slide.slideEl === clickedSlideEl);

  // Pause any inline gallery video so it doesn't play under the lightbox.
  const videoEls = galleryEl.querySelectorAll('video');
  videoEls.forEach((videoEl) => videoEl.pause());

  const lightboxInstance = getLightbox();
  lightboxInstance.loadAndOpen(Math.max(activeIndex, 0), slides.map((slide) => slide.data));

  lightboxInstance.on('destroy', () => {
    videoEls.forEach((videoEl) => videoEl.play());
  });
};

const initProductGalleryLightbox = () => {
  if (isLightboxInitialized) return;

  document.addEventListener('click', (event) => {
    if (event.target.closest('button, label, input')) return;

    const galleryEl = event.target.closest('[data-product-gallery]');
    if (!galleryEl) return;

    const clickedSlideEl = event.target.closest(SWIPER.SLIDE_CLASS);
    openGalleryLightbox(galleryEl, clickedSlideEl);
  });

  isLightboxInitialized = true;
};

export default initProductGalleryLightbox;
